import React, {
  createContext,
  useEffect,
  useState,
  ReactNode,
} from 'react';

export type RawIcon = {
  label: string;
  styles: string[];
  free?: string[];
  unicode: string;
  svg: {
    [styleName: string]: {
      path: string | string[];
      viewBox: number[];
      width: number;
      height: number;
      raw: string;
    };
  };
};

export type ProcessedIcon = {
  label: string;
  value: string;
  style: string;
  iconName: string;
  unicode: string;
  type: string;
  svgPath: string | string[];
  viewBox: number[];
  rawSvg: string;
};

type IconContextType = {
  icons: ProcessedIcon[];
  isLoading: boolean;
  error?: string;
  getIconByValue: (value: string) => ProcessedIcon | undefined;
};

function processFontAwesomeIcons(raw: Record<string, RawIcon>): ProcessedIcon[] {
  const out: ProcessedIcon[] = [];

  Object.entries(raw).forEach(([iconKey, icon]) => {
    const displayLabel = icon.label;

    const iconName = iconKey === displayLabel 
      ? displayLabel.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')
      : iconKey;

    const stylesToUse = icon.free?.length ? icon.free : icon.styles;

    stylesToUse.forEach((style) => {
      const svgData = icon.svg[style];
      if (!svgData) {
        console.warn(`Style '${style}' not found in SVG data for icon '${iconKey}'`);
        return;
      }

      out.push({
        label: displayLabel,
        value: `${style}:${iconName}`,
        style,
        iconName,
        unicode: icon.unicode,
        type: style,
        svgPath: svgData.path,
        viewBox: svgData.viewBox,
        rawSvg: svgData.raw,
      });
    });
  });

  return out.sort((a, b) => a.label.localeCompare(b.label));
}

export const IconContext = createContext<IconContextType>({
  icons: [],
  isLoading: true,
  getIconByValue: () => undefined,
});

export const IconProvider = ({ children }: { children: ReactNode }) => {
  const [icons, setIcons] = useState<ProcessedIcon[]>([]);
  const [isLoading, setLoad] = useState(true);
  const [error, setError] = useState<string>();

  const getIconByValue = (value: string): ProcessedIcon | undefined => {
    return icons.find(icon => icon.value === value);
  };

  useEffect(() => {
    const fetchIcons = async () => {
      try {
        const res = await fetch('/icons.json');
        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        const raw: Record<string, RawIcon> = await res.json();
        const processedIcons = processFontAwesomeIcons(raw);
        setIcons(processedIcons);
        
      } catch (err: any) {
        setError(err.message ?? 'Unknown error');
        console.error("Error fetching icons:", err);
      } finally {
        setLoad(false);
      }
    };

    fetchIcons();
  }, []);

  return (
    <IconContext.Provider value={{ icons, isLoading, error, getIconByValue }}>
      {children}
    </IconContext.Provider>
  );
};

export const useIcons = () => {
  const context = React.useContext(IconContext);
  if (!context) {
    throw new Error('useIcons must be used within an IconProvider');
  }
  return context;
};

export const IconPreview: React.FC<{ 
  icon: ProcessedIcon; 
  size?: number;
  className?: string;
}> = ({ icon, size = 16, className = "" }) => {
  const { svgPath, viewBox, style } = icon;
  
  const renderPath = () => {
    if (Array.isArray(svgPath)) {
      return svgPath.map((path, index) => (
        <path 
          key={index} 
          d={path} 
          className={index === 0 ? "fa-secondary" : "fa-primary"}
        />
      ));
    }
    return <path d={svgPath} />;
  };

  return (
    <svg
      width={size}
      height={size}
      viewBox={`${viewBox[0]} ${viewBox[1]} ${viewBox[2]} ${viewBox[3]}`}
      className={`icon-preview ${className}`}
      style={{ 
        fill: 'currentColor',
        ...(style === 'duotone' && {
          '--fa-secondary-opacity': '0.4'
        })
      }}
    >
      {style === 'duotone' && (
        <defs>
          <style>{`.fa-secondary{opacity:.4}`}</style>
        </defs>
      )}
      {renderPath()}
    </svg>
  );
};

export const parseIconValue = (value: string): { style: string; iconName: string } => {
  const [style, iconName] = value.split(':');
  return { style, iconName };
};

export const formatIconDisplay = (icon: ProcessedIcon): string => {
  return `${icon.label} (${icon.style})`;
};